import React from "react";
import { waUrl } from "../lib/constants";
import BackgroundRings from "../components/BackgroundRings";
import HeroWaves from "../components/HeroWaves";
import FadeIn from "../components/FadeIn";
import SessionCard from "../components/SessionCard";
import LocationColumn from "../components/LocationColumn";
import InsideSessionVideo from "../components/InsideSessionVideo";
import AnimalRecoverySection from "../components/AnimalRecoverySection";
import { Link } from "react-router-dom";
import { Moon, ArrowRight } from "lucide-react";

const TWO_AM_CARDS = [
    {
        eyebrow: "Sleep",
        title: "You haven't slept. Not really.",
        subhead: "20-30 minutes. You leave lighter. Sleep better tonight.",
        msg: "Hi, I'm not sleeping well. I'd like to reserve a session.",
    },
    {
        eyebrow: "Anxiety",
        title: "The anxiety is loud. Even at 3AM.",
        subhead: "Your nervous system finally quiets. You breathe again.",
        msg: "Hi, I'd like to reserve a session for anxiety relief.",
    },
    {
        eyebrow: "ADHD",
        title: "Your child can't focus. Can't settle.",
        subhead:
            "Kids leave calmer. Parents notice the difference. ADHD support.",
        msg: "Hi, I'd like to reserve a Kids / ADHD support session.",
    },
    {
        eyebrow: "OCD",
        title: "The loop won't break.",
        subhead:
            "Neural entrainment interrupts the cycle. Relief without medication.",
        msg: "Hi, I'd like to reserve a session for OCD support.",
    },
    {
        eyebrow: "Athletes",
        title: "Your body holds what training can't release.",
        subhead:
            "Golfers. Footballers. Swimmers. Basketballers. Fighters. Recovery while you rest.",
        msg: "Hi, I'm an athlete and I'd like to reserve a recovery session.",
    },
    {
        eyebrow: "Pain Relief",
        title: "The pain owns your day.",
        subhead:
            "Frequency-based relief. No pills. No side effects. Chronic and acute.",
        msg: "Hi, I'd like to reserve a session for pain relief.",
    },
    {
        eyebrow: "Migraine",
        title: "The migraine stops everything.",
        subhead: "Low-frequency intervention. Not another painkiller.",
        msg: "Hi, I'd like to reserve a session for migraine relief.",
    },
    {
        eyebrow: "Corporate",
        title: "Your team is running on empty.",
        subhead:
            "Teams that reset together, perform together. C-suite to crew.",
        msg: "Hi, I'd like to enquire about a Corporate Team session.",
    },
    {
        eyebrow: "Mind",
        title: "Your mind won't shut off.",
        subhead: "Neural entrainment. Not meditation. Just relief.",
        msg: "Hi, my mind won't quiet down. I'd like to reserve a session.",
    },
    {
        eyebrow: "Parents",
        title: "The parent who carries everyone.",
        subhead: "You carry everyone. 20 minutes to carry yourself.",
        msg: "Hi, I'd like to reserve a session — I'm a parent who needs a reset.",
    },
    {
        eyebrow: "Animals",
        title: "Your animal is anxious. So are you.",
        subhead:
            "Stables to sofas. Equine. Camel. Cats. Dogs. Same protocol. Same calm.",
        msg: "Hi, I'd like to reserve a session with my animal.",
    },
    {
        eyebrow: "Private",
        title: "For those who require complete privacy.",
        subhead:
            "In-villa sessions. HNWI. Referral-only. Request access via WhatsApp.",
        msg: "Hi, I'd like to request access to a Private / HNWI in-villa session.",
        cta: "Request Access →",
    },
];

const Home = () => {
    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section
                style={{
                    position: "relative",
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    padding: "140px 32px 80px",
                    overflow: "hidden",
                }}
                data-testid="hero-section"
            >
                <HeroWaves count={8} position="right" />
                <div
                    style={{
                        maxWidth: 1400,
                        margin: "0 auto",
                        width: "100%",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p
                            className="smallcaps"
                            style={{ marginBottom: 32 }}
                            data-testid="hero-tagline"
                        >
                            Recovery Through Precision Sound Recovery
                        </p>
                    </FadeIn>
                    <FadeIn delay={120}>
                        <h1
                            className="font-serif"
                            data-testid="hero-headline"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(40px, 7vw, 96px)",
                                fontWeight: 400,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                maxWidth: 1100,
                                marginBottom: 40,
                            }}
                        >
                            A sanctuary for the{" "}
                            <span style={{ color: "#c9a227" }}>
                                nervous system.
                            </span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={240}>
                        <div
                            style={{
                                color: "#888888",
                                fontSize: "clamp(16px, 1.6vw, 19px)",
                                lineHeight: 1.7,
                                maxWidth: 720,
                                marginBottom: 48,
                            }}
                            data-testid="hero-subheadlines"
                        >
                            <p style={{ margin: 0 }}>
                                For the parent who hasn't slept.
                            </p>
                            <p style={{ margin: 0 }}>
                                For the athlete whose body won't recover.
                            </p>
                            <p style={{ margin: 0 }}>
                                For the team running on empty.
                            </p>
                            <p style={{ margin: 0 }}>
                                For the corporate that never switches off.
                            </p>
                            <p style={{ margin: 0 }}>
                                For the animal that holds tension you feel too.
                            </p>
                        </div>
                    </FadeIn>
                    <FadeIn delay={360}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 16,
                                alignItems: "flex-start",
                            }}
                        >
                            <a
                                href={waUrl(
                                    "Hi, I'd like to reserve a session (demo, 20-30 min).",
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-gold-solid"
                                data-testid="hero-demo-cta"
                            >
                                Reserve Your Session
                            </a>
                            <p
                                style={{
                                    color: "#c9a227",
                                    fontSize: 13,
                                    margin: 0,
                                    lineHeight: 1.6,
                                    fontWeight: 400,
                                    letterSpacing: "0.02em",
                                }}
                                data-testid="hero-cta-tagline"
                            >
                                Feel the shift in your energy. Then pay.
                            </p>
                            <p
                                style={{
                                    color: "#888",
                                    fontSize: 14,
                                    margin: 0,
                                    lineHeight: 1.65,
                                    maxWidth: 560,
                                }}
                                data-testid="hero-cta-subtext"
                            >
                                20–30 minutes. No commitment. Same equipment.
                                Same practitioner. See how your nervous system
                                responds.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* TRUST BADGE — homepage only, after hero */}
            <section
                data-testid="trust-badge-section"
                style={{
                    padding: "56px 24px",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    borderBottom: "1px solid rgba(201,162,39,0.1)",
                    backgroundColor: "#0a0a0a",
                    position: "relative",
                }}
            >
                <FadeIn>
                    <div
                        data-testid="trust-badge"
                        style={{
                            maxWidth: 720,
                            margin: "0 auto",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 18,
                        }}
                    >
                        {/* Gold shield */}
                        <div
                            aria-hidden="true"
                            style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                            }}
                        >
                            <svg
                                width="64"
                                height="64"
                                viewBox="0 0 64 64"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs>
                                    <linearGradient
                                        id="trust-shield-grad"
                                        x1="0%"
                                        y1="0%"
                                        x2="0%"
                                        y2="100%"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#e0c060"
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#a68216"
                                        />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M32 4 L56 14 V32 C56 46 45 56 32 60 C19 56 8 46 8 32 V14 Z"
                                    fill="none"
                                    stroke="url(#trust-shield-grad)"
                                    strokeWidth="1.5"
                                />
                                <path
                                    d="M22 32 L29 39 L43 25"
                                    fill="none"
                                    stroke="#c9a227"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        {/* Big number */}
                        <p
                            className="font-serif"
                            style={{
                                color: "#c9a227",
                                fontSize: "clamp(48px, 8vw, 72px)",
                                fontWeight: 400,
                                margin: 0,
                                lineHeight: 1,
                                letterSpacing: "-0.02em",
                            }}
                            data-testid="trust-badge-number"
                        >
                            4,400+
                        </p>

                        {/* Label */}
                        <p
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(13px, 1.6vw, 15px)",
                                letterSpacing: "0.35em",
                                textTransform: "uppercase",
                                margin: 0,
                                fontWeight: 500,
                            }}
                            data-testid="trust-badge-label"
                        >
                            Reset Hours Delivered
                        </p>

                        {/* Divider */}
                        <span
                            aria-hidden="true"
                            style={{
                                display: "block",
                                width: 48,
                                height: 1,
                                backgroundColor: "rgba(201,162,39,0.5)",
                            }}
                        />

                        {/* Subtext — compliance-rewritten */}
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: "clamp(11px, 1.3vw, 13px)",
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                                fontWeight: 300,
                                lineHeight: 1.6,
                            }}
                            data-testid="trust-badge-subtext"
                        >
                            Precision Frequency Recovery, delivered with
                            precision.
                        </p>
                    </div>
                </FadeIn>
            </section>

            {/* THE 2AM SEARCH — 12 cards in 4 columns */}
            <section
                style={{
                    padding: "120px 32px",
                    position: "relative",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                }}
                data-testid="two-am-section"
            >
                <div style={{ maxWidth: 1400, margin: "0 auto" }}>
                    <FadeIn>
                        <p
                            className="eyebrow"
                            style={{ marginBottom: 20 }}
                            data-testid="two-am-eyebrow"
                        >
                            The Search
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 4.5vw, 56px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                maxWidth: 800,
                                marginBottom: 64,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            What brings you here. We already know.
                        </h2>
                    </FadeIn>
                    <div
                        className="two-am-grid"
                        style={{
                            display: "grid",
                            gap: 1,
                            backgroundColor: "rgba(201,162,39,0.08)",
                        }}
                    >
                        {TWO_AM_CARDS.map((c, i) => (
                            <FadeIn key={i} delay={i * 50}>
                                <SessionCard
                                    eyebrow={c.eyebrow}
                                    title={c.title}
                                    subhead={c.subhead}
                                    message={c.msg}
                                    cta={c.cta}
                                    testId={`two-am-card-${i + 1}`}
                                />
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section
                style={{
                    padding: "120px 32px",
                    backgroundColor: "#070707",
                    position: "relative",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    overflow: "hidden",
                }}
                data-testid="testimonials-section"
            >
                <BackgroundRings opacity={0.05} position="left" />
                <div
                    style={{
                        maxWidth: 1400,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p
                            className="eyebrow"
                            style={{ marginBottom: 20 }}
                            data-testid="testimonials-eyebrow"
                        >
                            Verified · Google Reviews
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 4.5vw, 56px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                maxWidth: 800,
                                marginBottom: 72,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            What people say after 20 minutes.
                        </h2>
                    </FadeIn>
                    <div
                        className="testimonials-grid"
                        style={{
                            display: "grid",
                            gap: 1,
                            backgroundColor: "rgba(201,162,39,0.08)",
                        }}
                    >
                        {[
                            {
                                quote: "I left my body. Not metaphorically. I was watching myself from above the bed. Then I came back. Lighter.",
                                name: "James",
                                meta: "Dubai",
                            },
                            {
                                quote: "Brain fog — gone. Bliss — unexpected. I didn't know I could feel this clear.",
                                name: "Jeetendra",
                                meta: "",
                            },
                            {
                                quote: "Hungover. Skeptical. 20 minutes later — neither. Just reset.",
                                name: "Veer",
                                meta: "",
                            },
                            {
                                quote: "I'm a physiotherapist. I've tried every recovery modality. This shanti — I've never felt it before.",
                                name: "Akshay Kumar",
                                meta: "Physiotherapist",
                            },
                            {
                                quote: "Three years of insomnia. One session. Slept through the night for the first time.",
                                name: "Sarah",
                                meta: "Abu Dhabi",
                            },
                            {
                                quote: "I trade crypto. My mind runs 18 hours a day. 20 minutes on that bed and my nervous system finally shuts off. I sleep now.",
                                name: "Michael",
                                meta: "Dubai",
                            },
                            {
                                quote: "Footballer. Hamstring held me back for months. One session. The next morning I trained without pain. My body finally let go.",
                                name: "Omar",
                                meta: "Sharjah",
                            },
                        ].map((t, i) => (
                            <FadeIn key={i} delay={i * 60}>
                                <figure
                                    data-testid={`testimonial-${i + 1}`}
                                    style={{
                                        backgroundColor: "#070707",
                                        padding: "44px 36px",
                                        margin: 0,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        gap: 24,
                                    }}
                                >
                                    <blockquote
                                        className="font-serif"
                                        style={{
                                            color: "#ffffff",
                                            fontSize:
                                                "clamp(20px, 1.7vw, 24px)",
                                            lineHeight: 1.5,
                                            fontWeight: 400,
                                            letterSpacing: "-0.01em",
                                            margin: 0,
                                            position: "relative",
                                        }}
                                    >
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                color: "#c9a227",
                                                fontSize: "1.2em",
                                                marginRight: 6,
                                                lineHeight: 1,
                                            }}
                                        >
                                            “
                                        </span>
                                        {t.quote}
                                    </blockquote>
                                    <figcaption
                                        style={{
                                            color: "#c9a227",
                                            fontSize: 11,
                                            letterSpacing: "0.3em",
                                            textTransform: "uppercase",
                                            fontWeight: 500,
                                        }}
                                    >
                                        — {t.name}
                                        {t.meta ? `, ${t.meta}` : ""}
                                    </figcaption>
                                </figure>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={480}>
                        <div
                            data-testid="google-reviews-link-wrap"
                            style={{
                                marginTop: 48,
                                textAlign: "center",
                            }}
                        >
                            <a
                                href="https://www.google.com/maps/search/?api=1&query=The+Reset+Sanctuary+Ras+Al+Khaimah"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="google-reviews-link"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "16px 32px",
                                    border: "1px solid rgba(201,162,39,0.35)",
                                    color: "#c9a227",
                                    fontSize: 13,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                    textDecoration: "none",
                                    fontWeight: 500,
                                    transition: "all 220ms ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "rgba(201,162,39,0.08)";
                                    e.currentTarget.style.borderColor =
                                        "rgba(201,162,39,0.6)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "transparent";
                                    e.currentTarget.style.borderColor =
                                        "rgba(201,162,39,0.35)";
                                }}
                            >
                                Read all reviews on Google →
                            </a>
                            <p
                                style={{
                                    marginTop: 16,
                                    color: "#888",
                                    fontSize: 12,
                                    letterSpacing: "0.08em",
                                    fontWeight: 300,
                                }}
                            >
                                Every voice above is a real, verified Google
                                review from a real client.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* INSIDE A SESSION — vertical video */}
            <section
                style={{
                    padding: "120px 32px",
                    backgroundColor: "#0a0a0a",
                    position: "relative",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    overflow: "hidden",
                }}
                data-testid="inside-session-section"
            >
                <BackgroundRings opacity={0.04} position="right" />
                <div
                    style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                        display: "grid",
                        gridTemplateColumns:
                            "minmax(0, 1fr) minmax(280px, 420px)",
                        gap: 64,
                        alignItems: "center",
                    }}
                    className="inside-session-grid"
                >
                    <FadeIn>
                        <div>
                            <p
                                className="eyebrow"
                                style={{ marginBottom: 20 }}
                                data-testid="inside-session-eyebrow"
                            >
                                Inside a Session
                            </p>
                            <h2
                                className="font-serif"
                                style={{
                                    color: "#ffffff",
                                    fontSize: "clamp(32px, 4.5vw, 56px)",
                                    fontWeight: 400,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.02em",
                                    marginBottom: 28,
                                    maxWidth: 620,
                                }}
                                data-testid="inside-session-headline"
                            >
                                The protocol.{" "}
                                <span style={{ color: "#c9a227" }}>
                                    Watch it work.
                                </span>
                            </h2>
                            <p
                                style={{
                                    color: "#bbb",
                                    fontSize: 18,
                                    lineHeight: 1.7,
                                    maxWidth: 540,
                                    marginBottom: 18,
                                    fontWeight: 300,
                                }}
                                data-testid="inside-session-body"
                            >
                                A real session, filmed in the UAE.
                                Frequencies engaged. Nervous system shifting.
                                Client deep in rest within minutes.
                            </p>
                            <p
                                style={{
                                    color: "#c9a227",
                                    fontSize: 11,
                                    letterSpacing: "0.32em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 600,
                                }}
                                data-testid="inside-session-no-ai"
                            >
                                Hand-produced. Human. No AI.
                            </p>
                        </div>
                    </FadeIn>
                    <FadeIn delay={120}>
                        <InsideSessionVideo
                            src="/brochure-assets/essam.mp4"
                            poster="/brochure-assets/essam-poster.jpg"
                            testId="home-inside-session-video"
                        />
                    </FadeIn>
                </div>
            </section>

            {/* THE WORK */}
            <section
                style={{
                    padding: "120px 32px",
                    backgroundColor: "#0a0a0a",
                    position: "relative",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                }}
                data-testid="the-work-section"
            >
                <div style={{ maxWidth: 1400, margin: "0 auto" }}>
                    <FadeIn>
                        <p
                            className="eyebrow"
                            style={{ marginBottom: 20 }}
                        >
                            The Work
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 4.5vw, 56px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                maxWidth: 800,
                                marginBottom: 64,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Sessions by location.
                        </h2>
                    </FadeIn>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(300px, 1fr))",
                            gap: 24,
                        }}
                    >
                        <FadeIn>
                            <LocationColumn
                                testId="location-rak"
                                title="Al Hamra"
                                location="Ras Al Khaimah"
                                message="Hi, I'd like to reserve a session at Al Hamra, Ras Al Khaimah."
                                items={[
                                    { name: "Demo", note: "20–30 min" },
                                    "Single Session",
                                    "Group Session",
                                    "Kids Session",
                                    "Equine Session",
                                    "Camel Session",
                                    "Domestic Companion Session",
                                    "Corporate Session",
                                ]}
                            />
                        </FadeIn>
                        <FadeIn delay={120}>
                            <LocationColumn
                                testId="location-dubai"
                                title="Al Furjan"
                                location="Dubai"
                                message="Hi, I'd like to reserve a session at Al Furjan, Dubai."
                                items={[
                                    "Single Session",
                                    "Group Session",
                                    "Corporate Session",
                                    "Private Session",
                                ]}
                            />
                        </FadeIn>
                        <FadeIn delay={240}>
                            <LocationColumn
                                testId="location-villa"
                                title="In-Villa"
                                location="UAE-wide"
                                message="Hi, I'd like to enquire about an In-Villa session (UAE-wide)."
                                items={[
                                    {
                                        name: "Private HNWI Session",
                                        note: "90 min",
                                    },
                                    "Corporate Retreat",
                                    "Equine Session",
                                    "Camel Session",
                                    "Domestic Companion Session",
                                ]}
                            />
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ANIMAL RECOVERY — Two Tiers */}
            <AnimalRecoverySection testIdPrefix="home-animal" />

            {/* INSIGHT TIMER */}
            <section
                style={{
                    padding: "120px 32px",
                    backgroundColor: "#070707",
                    position: "relative",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    borderBottom: "1px solid rgba(201,162,39,0.1)",
                    overflow: "hidden",
                }}
                data-testid="insight-timer-section"
            >
                <BackgroundRings opacity={0.05} position="right" />
                <div
                    style={{
                        maxWidth: 1100,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 20 }}>
                            On Insight Timer
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(36px, 5vw, 64px)",
                                fontWeight: 400,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                marginBottom: 28,
                                maxWidth: 800,
                            }}
                        >
                            Listen before you{" "}
                            <span style={{ color: "#c9a227" }}>arrive.</span>
                        </h2>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 11,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 24px",
                                fontWeight: 600,
                            }}
                            data-testid="insight-timer-badge"
                        >
                            Tracks Featured on Insight Timer · The World's
                            Largest Meditation App (Forbes, NY Times)
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 760,
                                marginBottom: 40,
                                fontWeight: 300,
                            }}
                        >
                            150M+ aggregate streams. 182K monthly listeners on Spotify.
                            Featured on BBC Radio 1 by Bobby Friction, BBC
                            Asian Network's Pathaan's Musical Rickshaw, and
                            Rolling Stone India. Curated alongside Talvin
                            Singh and DJ Spooky. Featured tracks for sleep,
                            anxiety, and recovery.
                        </p>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 18,
                                alignItems: "center",
                            }}
                        >
                            <a
                                href="https://www.insighttimer.com/resetsanctuary"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-gold-solid"
                                data-testid="insight-timer-cta"
                            >
                                Listen on Insight Timer
                            </a>
                            <Link
                                to="/sleep"
                                data-testid="home-sleep-cta"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 10,
                                    color: "#c9a227",
                                    textDecoration: "none",
                                    fontSize: 13,
                                    letterSpacing: "0.26em",
                                    textTransform: "uppercase",
                                    fontWeight: 600,
                                    fontFamily: "Inter, sans-serif",
                                    borderBottom:
                                        "1px solid rgba(201,162,39,0.4)",
                                    paddingBottom: 4,
                                }}
                            >
                                <Moon size={14} />
                                Get the Free 8-Hour Sleep Track
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* THE PRACTITIONER */}
            <section
                style={{
                    padding: "120px 32px",
                    position: "relative",
                    overflow: "hidden",
                }}
                data-testid="practitioner-section"
            >
                <BackgroundRings opacity={0.06} position="left" />
                <div
                    style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                        display: "grid",
                        gridTemplateColumns:
                            "minmax(280px, 380px) minmax(0, 1fr)",
                        gap: 64,
                        alignItems: "center",
                    }}
                    className="practitioner-grid"
                >
                    <FadeIn>
                        <div
                            data-testid="practitioner-photo-wrap"
                            style={{
                                position: "relative",
                                paddingTop: "100%",
                                overflow: "hidden",
                                border: "1px solid rgba(201,162,39,0.3)",
                            }}
                        >
                            <img
                                src="/brochure-assets/tatva.png"
                                alt="Tatva K"
                                data-testid="practitioner-photo"
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                            <div
                                aria-hidden="true"
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    background:
                                        "linear-gradient(180deg, rgba(10,10,10,0) 60%, rgba(10,10,10,0.5) 100%)",
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                    </FadeIn>
                    <FadeIn delay={120}>
                        <p
                            className="eyebrow"
                            style={{ marginBottom: 20 }}
                        >
                            The Practitioner
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(48px, 7vw, 88px)",
                                fontWeight: 400,
                                lineHeight: 1,
                                marginBottom: 32,
                                letterSpacing: "-0.03em",
                            }}
                            data-testid="practitioner-name"
                        >
                            Tatva K
                        </h2>
                        <p
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(18px, 1.8vw, 22px)",
                                lineHeight: 1.55,
                                maxWidth: 620,
                                marginBottom: 24,
                                fontWeight: 300,
                            }}
                            data-testid="practitioner-credentials"
                        >
                            Artist. 150M+ aggregate streams. 182K monthly Spotify
                            listeners. TEDx Speaker. Tracks Featured on
                            Insight Timer. ISTA &amp; IPHM Certified. Reiki
                            Grandmaster. Berklee College of Music — Music
                            Therapy Masterclass.
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 15,
                                lineHeight: 1.7,
                                maxWidth: 620,
                                marginBottom: 24,
                                fontWeight: 300,
                            }}
                            data-testid="practitioner-press"
                        >
                            Featured on BBC Radio 1 (Bobby Friction), BBC
                            Asian Network's Pathaan's Musical Rickshaw, and
                            Rolling Stone India. Curated alongside Talvin
                            Singh and DJ Spooky.
                        </p>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 11,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 24px",
                                fontWeight: 600,
                            }}
                            data-testid="practitioner-no-ai"
                        >
                            Hand-produced. Human. No AI.
                        </p>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 16,
                                letterSpacing: "0.05em",
                            }}
                            data-testid="practitioner-locations"
                        >
                            Al Hamra, Ras Al Khaimah and Al Furjan, Dubai.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* PRIVATE SESSIONS */}
            <section
                style={{
                    padding: "120px 32px",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    borderBottom: "1px solid rgba(201,162,39,0.1)",
                    backgroundColor: "#070707",
                }}
                data-testid="private-sessions-section"
            >
                <div
                    style={{
                        maxWidth: 900,
                        margin: "0 auto",
                        textAlign: "left",
                    }}
                >
                    <FadeIn>
                        <p
                            className="eyebrow"
                            style={{ marginBottom: 20 }}
                            data-testid="private-sessions-eyebrow"
                        >
                            Private Sessions
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(36px, 5vw, 64px)",
                                fontWeight: 400,
                                lineHeight: 1.05,
                                marginBottom: 28,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Private Sessions.
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 20,
                                lineHeight: 1.6,
                                marginBottom: 20,
                                fontWeight: 300,
                            }}
                        >
                            For those who require complete privacy and a full
                            reset.
                        </p>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 16,
                                lineHeight: 1.75,
                                marginBottom: 40,
                                maxWidth: 700,
                            }}
                        >
                            In-villa sessions available across the UAE.
                            Corporate retreats. Equine and camel sessions.
                            Referral preferred. Request access via WhatsApp.
                        </p>
                        <a
                            href={waUrl(
                                "Hi, I'd like to request access to a Private Session (in-villa / referral).",
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gold-solid"
                            data-testid="private-sessions-cta"
                        >
                            Request Access
                        </a>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Home;
